import React, { useState } from "react";
import { useTheme } from "../context/theme";
import logo from "../assets/valuate-logo.png";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn, Mail, Lock } from "lucide-react";
import Footer from "@/components/Footer";
import HeaderPage from "@/components/HeaderPages";

export default function LoginPage() {
  const [upiNumber, setUpiNumber] = useState("");
  const [activeTab, setActiveTab] = useState("client"); // 'client' or 'team'
  const [idNumber, setIdNumber] = useState(""); // Fixed variable name
  const [isLoading, setIsLoading] = useState(false);
  const [showIdNumber, setShowIdNumber] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeInput, setActiveInput] = useState("");
  const navigate = useNavigate();
  const { theme } = useTheme();
  const BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  const themeStyles = {
    bgColor:
      theme === "dark"
        ? "bg-gray-400"
        : "bg-gradient-to-br from-gray-50 to-gray-50",
    cardBg: theme === "dark" ? "bg-gray-400" : "bg-white",
    textColor: theme === "dark" ? "text-white" : "text-gray-500",
    inputBg: theme === "dark" ? "bg-gray-700" : "bg-white",
    borderColor: theme === "dark" ? "border-gray-600" : "border-gray-200",
    buttonBg:
      theme === "dark"
        ? "mt-4 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-300 shadow-md hover:shadow-lg"
        : "mt-4 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-300 shadow-md hover:shadow-lg",
    buttonHover:
      theme === "dark"
        ? "hover:bg-gray-700"
        : "hover:from-gray-700 hover:to-gray-700",
    imageBg:
      theme === "dark"
        ? "bg-gray-700"
        : "bg-gradient-to-br from-gray-300 to-gray-700",
    labelColor: theme === "dark" ? "text-gray-300" : "text-gray-600",
    linkColor: theme === "dark" ? "text-blue-400" : "text-blue-600",
  };

  // UPI validation function
  const isUPI = (input: string) => {
    const regex = /^\d{1,3}\/\d{2}\/\d{2}\/\d{2}\/\d{4}$/;

    return regex.test(input);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (activeTab === "client") {
      if (!upiNumber || !idNumber) {
        setError("Please enter both UPI number and ID number");
        setIsLoading(false);
        return;
      }

      // Validate UPI number format
      if (!isUPI(upiNumber)) {
        setError("Please enter a valid UPI ID (e.g., x/xx/xx/xx/xxxx)");
        setIsLoading(false);
        return;
      }

      try {
        // Prepare login data
        const loginData = {
          upiNumber: upiNumber,
          password: idNumber,
        };

        const response = await fetch(`${BASE_URL}/auth/login-client`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginData),
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(
            responseData.message ||
              `Login failed with status: ${response.status}`
          );
        }

        // Store authentication data
        localStorage.setItem("valuation_token", responseData.token);
        localStorage.setItem(
          "valuation_user",
          JSON.stringify(responseData.user)
        );

        // Redirect based on user role
        switch (responseData.user.role) {
          case "DATA_COLLECTOR":
            navigate("/data-dashboard", { replace: true });
            break;
          case "SUPERVISOR":
            navigate("/supervisor-dashboard", { replace: true });
            break;
          case "ADMIN":
            navigate("/admin-dashboard", { replace: true });
            break;
          default:
            navigate("/client-dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Login error:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An unknown error occurred during login. Please check your connection and try again."
        );
      } finally {
        setIsLoading(false);
      }
    } else if (activeTab === "team") {
      if (!email || !password) {
        setError("Please enter both email and password");
        setIsLoading(false);
        return;
      }

      try {
        // Prepare login data
        const loginData = {
          email: email,
          password: password,
        };

        const response = await fetch(`${BASE_URL}/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginData),
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(
            responseData.message ||
              `Login failed with status: ${response.status}`
          );
        }

        // Store authentication data
        localStorage.setItem("valuation_token", responseData.token);
        localStorage.setItem(
          "valuation_user",
          JSON.stringify(responseData.user)
        );

        // Redirect based on user role
        switch (responseData.user.role) {
          case "DATA_COLLECTOR":
            navigate("/data-dashboard");
            window.location.reload();
            break;
          case "SUPERVISOR":
            navigate("/supervisor-dashboard");
            window.location.reload();
            break;
          case "ADMIN":
            navigate("/admin-dashboard");
            window.location.reload();
            break;

          case "CLIENT":
            navigate("/client-dashboard");
            window.location.reload();
            break;

          case "INSTITUTIONAL_CLIENT":
            navigate("/institutional-client-dashboard");
            window.location.reload();
            break;
          default:
            navigate("/dashboard");
            window.location.reload();
        }
      } catch (err) {
        console.error("Login error:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An unknown error occurred during login. Please check your connection and try again."
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div>
      {/* <HeaderPage /> */}

      <div
        className={`min-h-screen ${themeStyles.bgColor} flex items-center justify-center p-4`}
      >
        <div
          className={`w-full max-w-5xl ${themeStyles.cardBg} rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row`}
        >
          <div
            className={`w-full md:w-1/2 ${themeStyles.imageBg} flex items-center justify-center p-4 relative overflow-hidden`}
          >
            <div className="absolute inset-0 bg-gray-400 z-10"></div>
            <div
              className="relative z-20 w-full text-white text-center" 
              style={{
                backgroundImage: `url('valuate-logo_2.png')`,
                backgroundSize: "60%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="b-20">
                {/* <div className="inline-flex items-center justify-center bg-white backdrop-blur-sm p-3 rounded-full mb-3">
                  <img src={logo} alt="Value Logo" className="h-16 w-16" />
                </div> */}
                {/* <h1 className="text-2xl font-bold mb-1">Value</h1> */}
                <p className="text-black font-bold text-sm pt-44">
                  Real-Time Property Valuation
                </p>
              </div>

              {/* <img
              src={loginImage}
              alt="Property Valuation"
              className="w-full h-32 object-cover rounded-lg shadow-lg mb-4"
            /> */}

              <div className="mb-3"></div>
              {activeTab === "client" && (
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Link to="/property/submit">
                    <div className="bg-gray-600 backdrop-blur-sm rounded p-2 text-center">
                      {/* <item.icon className="h-3 w-3 mx-auto" /> */}
                      <p className="text-sm">Submit New Property</p>
                    </div>
                  </Link>

                  <Link to="/demo">
                    <div className="bg-gray-600 backdrop-blur-sm rounded p-2 text-center">
                      {/* <item.icon className="h-3 w-3 mx-auto" /> */}
                      <p className="text-sm">View Demo</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
            <div
              className={`w-full max-w-md ${
                themeStyles.cardBg
              } rounded-2xl shadow-xl p-6 md:p-8 ${
                theme === "light" ? "shadow-gray-400" : ""
              }`}
            >
              <div className="text-center mb-6">
                <h2
                  className={`text-xl font-bold mb-2 ${themeStyles.textColor}`}
                >
                  Login To Your Account
                </h2>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start text-sm">
                  <svg
                    className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {activeTab === "client" ? (
                // Client login form
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* UPI number field */}
                  <div>
                    <label
                      className={`block mb-2 text-sm font-medium ${themeStyles.labelColor}`}
                    >
                      UPI Number
                    </label>
                    <div
                      className={`relative rounded-lg transition-all duration-200 ${
                        activeInput === "upi"
                          ? "ring-2 ring-blue-500 ring-opacity-50"
                          : ""
                      }`}
                    >
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={upiNumber}
                        onChange={(e) => setUpiNumber(e.target.value)}
                        onFocus={() => setActiveInput("upi")}
                        onBlur={() => setActiveInput("")}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm ${themeStyles.inputBg} ${themeStyles.borderColor} border focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                        placeholder="Enter UPI Number"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Format: (e.g., x/xx/xx/xx/xxxx)
                    </p>
                  </div>

                  {/* ID number field */}
                  <div>
                    <label
                      className={`block mb-2 text-sm font-medium ${themeStyles.labelColor}`}
                    >
                      ID Number/ TIN Number
                    </label>
                    <div
                      className={`relative rounded-lg transition-all duration-200 ${
                        activeInput === "idNumber"
                          ? "ring-2 ring-blue-500 ring-opacity-50"
                          : ""
                      }`}
                    >
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type={showIdNumber ? "text" : "password"}
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value)}
                        onFocus={() => setActiveInput("idNumber")}
                        onBlur={() => setActiveInput("")}
                        className={`w-full pl-10 pr-12 py-3 rounded-lg text-sm ${themeStyles.inputBg} ${themeStyles.borderColor} border focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                        placeholder="Enter Your ID Number"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowIdNumber(!showIdNumber)}
                      >
                        {showIdNumber ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Login button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 px-4 ${themeStyles.buttonBg} ${
                      themeStyles.buttonHover
                    } text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center shadow-md ${
                      isLoading
                        ? "opacity-80 cursor-not-allowed"
                        : "hover:scale-[1.02]"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Login...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2" />
                        Login
                      </>
                    )}
                  </button>
                </form>
              ) : (
                // Team login form
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email field */}
                  <div>
                    <label
                      className={`block mb-2 text-sm font-medium ${themeStyles.labelColor}`}
                    >
                      Email
                    </label>
                    <div
                      className={`relative rounded-lg transition-all duration-200 ${
                        activeInput === "email"
                          ? "ring-2 ring-blue-500 ring-opacity-50"
                          : ""
                      }`}
                    >
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setActiveInput("email")}
                        onBlur={() => setActiveInput("")}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm ${themeStyles.inputBg} ${themeStyles.borderColor} border focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                        placeholder="Enter Email"
                        required
                      />
                    </div>
                  </div>

                  {/* Password field */}
                  <div>
                    <label
                      className={`block mb-2 text-sm font-medium ${themeStyles.labelColor}`}
                    >
                      Password
                    </label>
                    <div
                      className={`relative rounded-lg transition-all duration-200 ${
                        activeInput === "password"
                          ? "ring-2 ring-blue-500 ring-opacity-50"
                          : ""
                      }`}
                    >
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type={showIdNumber ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setActiveInput("password")}
                        onBlur={() => setActiveInput("")}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm ${themeStyles.inputBg} ${themeStyles.borderColor} border focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                        placeholder="Enter Password"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowIdNumber(!showIdNumber)}
                      >
                        {showIdNumber ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Login button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 px-4 ${themeStyles.buttonBg} ${
                      themeStyles.buttonHover
                    } text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center shadow-md ${
                      isLoading
                        ? "opacity-80 cursor-not-allowed"
                        : "hover:scale-[1.02]"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Login...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2" />
                        Login
                      </>
                    )}
                  </button>
                </form>
              )}
              {activeTab === "client" && (
                <div
                  className={`mt-6 pt-4 border-t ${themeStyles.borderColor}`}
                >
                  <p
                    className={`text-xs text-center ${themeStyles.labelColor}`}
                  >
                    <span
                      onClick={() => setActiveTab("team")}
                      className={`font-medium ${themeStyles.linkColor} cursor-pointer hover:underline transition-colors`}
                    >
                      Private access
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </div>
  );
}
